import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { observable, makeObservable } from 'mobx';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { ListItem } from './SelectionRabItem';
import { MarketRabView } from 'src/domains/sportsbook/betting/state/rabState/Types';
import {
    MoreLessGroup,
    LinkMoreLessWrapper,
} from 'src/domains/sportsbook/webview/components/eventMarkets/marketGroup/outrightMarket/OutrightMarket.style';
import {
    SelectionGroupWrapper,
    SelectionRabList,
    MarketGroup,
    SelectionsGroup,
    ChevronIconMoreWrapper,
} from './SelectionRab.style';
import { BetslipRabId } from 'src/domains/sportsbook/betting/models/BetslipIdModels';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Collapse } from 'src/ui/components/collapse/sports/04/Collapse';

interface SelectionRabPropsType {
    rabId: BetslipRabId;
    rabMarket: MarketRabView;
    order: number;
}

class State {
    @observable public collapsed: boolean = false;
    @observable public moreOpen: boolean = false;

    public constructor(order: number) {
        makeObservable(this);
        this.collapsed = order > 4;
    }

    public toggleCollapsed = (): void => {
        if (this.collapsed) {
            this.collapsed = false;
            this.moreOpen = false;
        } else {
            this.collapsed = true;
        }
    };

    public onClickMore = (event: React.SyntheticEvent): void => {
        event.stopPropagation();
        event.preventDefault();

        this.moreOpen = true;
    };

    public onClickLess = (event: React.SyntheticEvent): void => {
        event.stopPropagation();
        event.preventDefault();

        this.moreOpen = false;
    };
}

const renderMore = (state: State, sportsbookVariant: DefaultComponentVariantType | null): React.ReactNode => {
    return (
        <MoreLessGroup variant={sportsbookVariant}>
            <LinkMoreLessWrapper
                onClick={state.onClickMore}
                variant={sportsbookVariant}
            >
                <I18n
                    langKey='events.request-a-bet-selection.more'
                    defaultText='More'
                />
                <ChevronIconMoreWrapper position='down' />
            </LinkMoreLessWrapper>
        </MoreLessGroup>
    );
};

const renderLess = (state: State, sportsbookVariant: DefaultComponentVariantType | null): React.ReactNode => {
    return (
        <MoreLessGroup variant={sportsbookVariant}>
            <LinkMoreLessWrapper
                onClick={state.onClickLess}
                variant={sportsbookVariant}
            >
                <I18n
                    langKey='events.request-a-bet-selection.less'
                    defaultText='Less'
                />
                <ChevronIconMoreWrapper position='up' />
            </LinkMoreLessWrapper>
        </MoreLessGroup>
    );
};

const renderMoreLess = (state: State, sportsbookVariant: DefaultComponentVariantType | null): React.ReactNode => {
    const { moreOpen } = state;
    if (moreOpen) {
        return renderLess(state, sportsbookVariant);
    }

    return renderMore(state, sportsbookVariant);
};

export const SelectionRab = observer('SelectionRab', (props: SelectionRabPropsType) => {
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const { rabMarket, rabId, order } = props;
    const [state] = React.useState(() => new State(order));
    const { collapsed, moreOpen } = state;
    const { sportsbookVariant } = configState.componentVariants;

    const selectionsMoreLess = moreOpen ? rabMarket.selections : rabMarket.selections.slice(0, 4);

    return (
        <MarketGroup
            data-test-rab-market-name={rabMarket.name}
            data-test-collapsed={collapsed}
            variant={configState.componentVariants.sportsbookVariant}
        >
            <Collapse
                handleToggle={state.toggleCollapsed}
                isOpen={collapsed === false}
                title={rabMarket.name}
                dataTest='collapse-market'
            />

            {collapsed ? null : (
                <SelectionGroupWrapper variant={configState.componentVariants.sportsbookVariant}>
                    <SelectionsGroup variant={sportsbookVariant}>
                        <SelectionRabList
                            columns={rabMarket.viewDetails.selectionDisplayType}
                            variant={configState.componentVariants.sportsbookVariant}
                        >
                            {selectionsMoreLess.map((selection) => {
                                return (
                                    <ListItem
                                        key={`${rabMarket.name} ${selection.name}`}
                                        rabId={rabId}
                                        marketName={rabMarket.name}
                                        name={selection.name}
                                        isSelect={selection.isSelect}
                                        displayName={true}
                                    />
                                );
                            })}
                        </SelectionRabList>
                    </SelectionsGroup>

                    {rabMarket.selections.length > 4 ? renderMoreLess(state, sportsbookVariant) : null}
                </SelectionGroupWrapper>
            )}
        </MarketGroup>
    );
});
